import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  ApiClient, AussenBerechnungslauf, AussenBerichtsstand,
} from '../api';
import {
  artname, bereichname, bewertungname, herkunftname, kreisname, seitenname,
} from '../aussenKlartext';
import { AussenBerichtKnopf } from './aussenanlagen/AussenBerichtKnopf';
import {
  ContentSection, DataTable, EmptyState, KpiCard, KpiReihe, formatiereZahl,
  LoadingState, PropertyGrid, StatusBadge, WertEinheit, type Spalte, type Vorbehalt,
} from '../muster';
import { Rechenstand, aussenFachvorbehalt, zeitpunkt } from './sanitaer/kopf';
import { OHNE_LAUF, berichtZiel, bewertungStatus, useAussenKopf } from './aussenanlagen/kopf';
import './aussenanlagen/aussen.css';

/**
 * Die Berechnungen der Außenanlagen.
 *
 * --- Aufbau nach liNear ---------------------------------------------------
 *
 * Oben das Ergebnis, darunter die Annahmen mit ihrer Herkunft, dann die
 * Stränge — und in jedem die Teilstrecken einzeln. Ein Berechnungsblatt, das
 * nur die Summe nennt, ist keine Berechnung, sondern eine Behauptung.
 *
 * --- Warum die Annahmen so weit oben stehen -------------------------------
 *
 * Ein Wärmeverlust von 3,4 kW sagt wenig. Ein Wärmeverlust von 3,4 kW bei
 * einer ANGENOMMENEN Bodentemperatur von 10 °C und einer ANGENOMMENEN
 * Verlegetiefe von 1,2 m sagt, was er wert ist.
 *
 * Die angenommenen Größen tragen die Marke „angenommen“ — sie haben keine
 * Quelle außerhalb dieser Rechnung.
 *
 * --- TS9b1b: kein eigener Kopf --------------------------------------------
 *
 * Die Seite ist ein Reiter der Außenanlagen (E1). Rechenstand des gewählten
 * Bereichs, Exportleiste (PDF-Bericht, Excel) und Vorbehalte gehen über
 * `useAussenKopf` in den Kopf des Rahmens; hier steht nur der Inhalt.
 *
 * --- TS9b2: der Bereich steht in der Adresse (Entscheidung E3) -------------
 *
 * `aussenanlagen/berechnung/:bereich` - ohne Bereich Freispiegel. Löschwasser
 * bekommt damit KEINE eigene Seite (die Gliederung N2 bleibt), ist aber unter
 * `…/berechnung/loeschwasser` direkt verlinkbar (TX-STANDARD S1.1: Reiter in
 * der Adresse). Die Bereichswahl und die Berichtsliste sind Verweise; ein
 * unbekannter Bereich in der Adresse wird nicht abgefragt und nicht gerechnet
 * (der Server rechnete sonst ALLE Bereiche), sondern als leer gemeldet.
 */

/**
 * Die SIEBEN Bereiche.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM SIEBEN UND NICHT SECHS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `verlegetiefe` fehlte bis zum 23.09.2026 in dieser Liste. Die Rechnung
 * fuhr ihn mit (`RechneAlleAsync`), der Endpunkt lieferte ihn, das PDF
 * konnte ihn setzen — nur anklicken konnte ihn niemand. Gemessen an der
 * Bestandskopie vom 23.09.2026: sieben Netze, alle sieben mit der Bewertung
 * `nicht_pruefbar`, weil das Gelände fehlt. Genau so ein Ergebnis darf
 * nicht unsichtbar sein.
 *
 * Die Norm hier ist die ERWARTETE. Auf dem Blatt steht die des LAUFS
 * (`lauf.norm`) — wer den Text pflegt und die Norm des Laufs nicht
 * danebenstellt, behauptet eine Norm, die der Lauf nicht geführt hat.
 */
const BEREICHE = [
  ['freispiegel', 'Freispiegelentwässerung', 'DIN EN 752 · DIN 1986-100'],
  ['loeschwasser', 'Löschwasserversorgung', 'DVGW W 405'],
  ['fernwaerme', 'Fernwärme', 'DIN EN 13941'],
  ['versickerung', 'Versickerung und Rückhaltung', 'DWA-A 138-1'],
  ['trinkwasser', 'Trinkwasseranschluss', 'DIN 1988-300 · DIN 14462'],
  ['leerrohr', 'Leerrohrnetz', 'DIN VDE 0100-520'],
  ['verlegetiefe', 'Verlegetiefe und Frostgrenze',
   'DIN EN 1610 · DIN 1988-200'],
] as const;

/** Die Spalten je Bereich. Eine gemeinsame Liste hätte lauter leere Felder. */
const SPALTEN: Record<string, [string, string, number?][]> = {
  // 'v [m/s]' ist die Geschwindigkeit bei TEILFÜLLUNG — die, die DIN EN 752
  // meint. Bis zum 16.09.2026 stand hier Vollstrom/Querschnitt, also die
  // Geschwindigkeit bei Vollfüllung, die es im Freispiegelbetrieb nie gibt.
  // Beide stehen jetzt nebeneinander, und daneben Bemessungsabfluss gegen
  // Vollstrom: Nachfrage und Angebot.
  freispiegel: [
    ['abschnittNr', 'Nr'],
    ['elementId', 'Element'],
    ['nennweite', 'DN'],
    ['innendurchmesserMm', 'di [mm]', 1],
    ['laengeM', 'L [m]', 2],
    ['gefaellePromille', 'J [‰]', 2],
    ['mindestgefaelleSoll', 'Soll [‰]', 1],
    ['abflussLs', 'Q̇ [l/s]', 2],
    ['fuellungsgrad', 'h/di', 3],
    ['geschwindigkeitMs', 'v [m/s]', 2],
    ['vollstromLs', 'Q̇voll [l/s]', 2],
    ['vollstromGeschwindigkeitMs', 'vvoll [m/s]', 2],
    ['istFallstueck', 'Fallstück'],
    ['bewertung', 'Bewertung'],

    // Der GRUND gehört neben die Bewertung: 238 der 282 Teilstrecken tragen
    // 'ohne_abfluss', und das ist keine Beanstandung, sondern eine
    // Nachweislücke. Ohne den Grund daneben sieht beides gleich aus.
    ['befund', 'Grund'],
  ],
  fernwaerme: [
    ['elementId', 'Element'],
    ['seite', 'Seite'],
    ['laengeM', 'L [m]', 2],
    ['daMm', 'da [mm]', 1],
    ['daemmstaerkeMm', 'Dämmung [mm]', 0],
    ['temperaturC', 'T [°C]', 0],
    ['verlustWM', 'q [W/m]', 2],
    ['verlustW', 'Q̇ [W]', 1],
    ['kopplungWM', 'Kopplung [W/m]', 2],
    ['herkunft', 'Herkunft'],
  ],
  trinkwasser: [
    ['nr', 'Nr'],
    ['elementId', 'Element'],
    ['nennweite', 'DN'],
    ['innendurchmesserMm', 'di [mm]', 1],
    ['laengeM', 'L [m]', 2],
    ['volumenstromLs', 'V̇ [l/s]', 3],
    ['geschwindigkeitMs', 'v [m/s]', 2],
    ['druckverlustBar', 'Δp [bar]', 4],
    ['bewertung', 'Art'],
  ],
  leerrohr: [
    ['aussendurchmesserMm', 'Kabel ⌀ [mm]', 0],
    ['maxStueck', 'max. Stück', 0],
    ['belegungProzent', 'Füllgrad [%]', 0],
    ['regel', 'Regel nach DIN VDE 0100-520'],
  ],
  versickerung: [
    ['bezeichnung', 'Teilfläche'],
    ['art', 'Art'],
    ['flaecheM2', 'A [m²]', 0],
    ['anteilProzent', 'Anteil [%]', 0],
    ['abflussbeiwert', 'ψ', 2],
    ['beitragM2', 'Beitrag [m²]', 1],
    ['herkunftFlaeche', 'Herkunft'],
    ['bestaetigt', 'bestätigt'],
  ],
};

export function AussenBerechnung({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const { bereich: ausAdresse } = useParams<{ bereich?: string }>();
  const bereich = ausAdresse ?? 'freispiegel';
  const navigate = useNavigate();
  const pfad = (k: string) => `/projekte/${projektId}/aussenanlagen/berechnung/${k}`;
  const [lauf, setLauf] = useState<AussenBerechnungslauf | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [berichte, setBerichte] = useState<AussenBerichtsstand[] | null>(null);

  // Die Berichtsliste ERZEUGT nichts - sie liest Lauf und Register. Sie
  // wird einmal geholt und nach jedem Rechnen neu, weil ein neuer Lauf
  // jeden vorhandenen Bericht ueberholt.
  const berichteLaden = () => {
    api.aussenBerichte(projektId)
      .then((x) => setBerichte(x.bereiche))
      .catch(() => setBerichte(null));
  };

  useEffect(berichteLaden, [api, projektId]);

  // Bekannt ist, was die Auswahl führt oder die Berichtsliste meldet.
  const bekannt = BEREICHE.some((b) => b[0] === bereich)
    || (berichte?.some((b) => b.bereich === bereich) ?? false);

  // Die Antwort gilt nur für den Bereich, nach dem gefragt wurde: Wer schnell
  // weiterklickt, bekäme sonst den Lauf des vorigen Bereichs unter dem neuen.
  const gefragt = useRef(bereich);
  const laden = () => {
    setFehler(null);
    const fuer = bereich;
    gefragt.current = fuer;
    api.aussenBerechnung(projektId, fuer)
      .then((l) => { if (gefragt.current === fuer) setLauf(l); })
      .catch((e) => {
        if (gefragt.current !== fuer) return;
        setLauf(null);
        setFehler(e instanceof Error ? e.message : String(e));
      });
  };

  useEffect(() => {
    setLauf(null);
    setFehler(null);
    if (bekannt) laden();
    else gefragt.current = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projektId, bereich, bekannt]);

  const rechnen = async () => {
    setLaeuft(true);
    try {
      await api.aussenRechnen(projektId, bereich);
      laden();
      berichteLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const name = BEREICHE.find((b) => b[0] === bereich)?.[1]
    ?? bereichname(bereich);

  // TS9a (TX-STANDARD S1.2): Kopf mit dem Rechenstand des gewählten Bereichs und der Exportleiste, darunter die
  // Vorbehalte - die offene Fachfrage (SAN-05/AUA-22, derselbe Satz wie die Excel-Mappe) und „veraltet“ (Server).
  // TS9b1b: alles drei geht an den Kopf des Rahmens (`useAussenKopf`).
  const vorbehalte: Vorbehalt[] = [];
  const frage = lauf ? aussenFachvorbehalt(bereich) : null;
  if (frage) vorbehalte.push(frage);
  if (lauf?.standVeraltet) {
    vorbehalte.push({
      art: 'veraltet',
      text: `Gerechnet am ${zeitpunkt(lauf.gerechnetAm)}, jüngste Außenaufnahme vom ${zeitpunkt(lauf.modellstand)}. `
        + 'Jede Zahl auf dieser Seite gilt für einen Modellstand, den es nicht mehr gibt. Über „neu rechnen“ entsteht '
        + 'der neue Stand.',
    });
  }

  // Der Bericht des Bereichs steht mit Dokumentnummer in der Exportleiste; ohne Lauf ist er gesperrt wie die Mappe.
  const stand = berichte?.find((b) => b.bereich === bereich) ?? null;
  const bericht = berichtZiel(api, projektId, bereich, stand);

  useAussenKopf({
    meta: lauf ? <Rechenstand lauf={lauf} veraltet={lauf.standVeraltet} />
      : fehler ? <Rechenstand lauf={null} /> : undefined,
    ziele: bekannt ? [
      lauf ? bericht : { ...bericht, gesperrt: { grund: OHNE_LAUF } },
      {
        // Der Lauf des Bereichs als Excel (TS11) - ohne Lauf gesperrt mit Grund.
        art: 'excel', beschriftung: 'Excel', laden: () => api.aussenBerechnungExcel(projektId, bereich),
        gesperrt: lauf ? undefined : { grund: 'Für diesen Bereich wurde noch nicht gerechnet.' },
      },
    ] : [],
    vorbehalte,
  }, [api, projektId, bereich, bekannt, lauf, fehler, stand]);

  return (
    <>
      <ContentSection
        titel="Bereiche"
        beschreibung="Oben das Ergebnis, darunter die Annahmen mit ihrer Herkunft, dann die Stränge — und in jedem die Teilstrecken einzeln."
        werkzeuge={(
          <div className="reiter aus-bereichswahl" role="tablist" aria-label="Bereiche der Berechnung">
            {BEREICHE.map(([k, bname]) => (
              <Link
                key={k}
                to={pfad(k)}
                role="tab"
                aria-selected={bereich === k}
                aria-current={bereich === k ? 'page' : undefined}
                className={`reiter-knopf${bereich === k ? ' aktiv' : ''}`}
              >
                {bname}
              </Link>
            ))}
          </div>
        )}
        aktionen={(
          <button type="button" className="m-knopf m-knopf-haupt" disabled={laeuft || !bekannt} onClick={rechnen}>
            {laeuft ? 'rechnet …' : 'neu rechnen'}
          </button>
        )}
        nackt
      >
        {laeuft && !lauf && (
          <LoadingState text={`${name} wird gerechnet …`} art="spinner" />
        )}
        {bekannt && !laeuft && !lauf && !fehler && (
          <LoadingState text={`Lauf „${name}“ wird abgerufen …`} />
        )}
      </ContentSection>

      {!bekannt && (
        <EmptyState
          titel="Diesen Bereich gibt es nicht."
          erklaerung={`„${bereich}“ ist kein Bereich der Außenberechnung. Die Bereiche stehen oben in der Auswahl.`}
          aktion={<Link className="m-knopf m-knopf-haupt" to={pfad('freispiegel')}>Zur Freispiegelentwässerung</Link>}
        />
      )}

      {fehler && (
        <EmptyState
          titel={`Für „${name}“ wurde noch keine Berechnung durchgeführt.`}
          erklaerung={'Die Rechnung entsteht aus dem aufgenommenen Außenanlagennetz. '
            + 'Ohne Lauf gibt es weder Annahmen noch Teilstrecken.'}
          aktion={(
            <button type="button" className="m-knopf m-knopf-haupt" disabled={laeuft} onClick={rechnen}>
              {laeuft ? 'rechnet …' : 'Berechnung starten'}
            </button>
          )}
        />
      )}

      {berichte && (
        <Berichtsliste
          api={api} projektId={projektId} berichte={berichte}
          gewaehlt={bereich} waehlen={(k) => navigate(pfad(k))}
        />
      )}

      {lauf && <Blatt lauf={lauf} bereich={bereich} />}
    </>
  );
}

/**
 * Alle SIEBEN Berichte auf einem Blatt.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIESE LISTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Den Berichtsendpunkt gibt es für alle sieben Bereiche. Erreichbar war er
 * über genau einen Knopf — und der galt dem Bereich, der gerade offen war.
 * Von den sieben kannte die Auswahlliste sechs: `verlegetiefe` fehlte, und
 * damit war sein Bericht (sieben Netze, alle sieben `nicht prüfbar`) über
 * die Oberfläche nicht zu bekommen.
 *
 * Die Liste zeigt je Bereich den Rechenstand, die Zahl der Fälle, die
 * beanstandeten darunter, die Dokumentnummer des letzten Berichts und ob
 * dieser noch zum jüngsten Lauf gehört. Sie ERZEUGT nichts — erst der Knopf
 * in der Zeile tut das.
 */
function Berichtsliste({ api, projektId, berichte, gewaehlt, waehlen }: {
  api: ApiClient; projektId: string;
  berichte: AussenBerichtsstand[];
  gewaehlt: string;
  waehlen: (b: string) => void;
}) {
  const mitLauf = berichte.filter((b) => b.hatLauf).length;

  return (
    <ContentSection
      titel={`Berichte der Außenanlagen (${mitLauf} von ${berichte.length})`}
      beschreibung={'Je Bereich ein PDF mit eigener Dokumentnummernfolge. '
        + 'Diese Liste erzeugt nichts — sie liest den Lauf und das '
        + 'Berichtsregister.'}
      aufklappbar
      standardOffen
    >
      <DataTable<AussenBerichtsstand>
        name="Berichte je Bereich"
        spalten={[
          {
            schluessel: 'bereich', titel: 'Bereich', immerSichtbar: true, wert: (b) => b.titel,
            inhalt: (b) => (
              <button type="button" className="m-knopf m-knopf-still" onClick={() => waehlen(b.bereich)}>
                {b.titel}
              </button>
            ),
          },
          { schluessel: 'norm', titel: 'Norm des Laufs', wert: (b) => b.norm },
          {
            schluessel: 'gerechnetAm', titel: 'Rechenstand', art: 'status',
            wert: (b) => (b.gerechnetAm ? zeitpunkt(b.gerechnetAm) : 'nicht gerechnet'),
            inhalt: (b) => (b.gerechnetAm
              ? zeitpunkt(b.gerechnetAm)
              : <StatusBadge zustand="neutral" text="nicht gerechnet" klein />),
          },
          { schluessel: 'zeilen', titel: 'Fälle', art: 'zahl', nachkomma: 0, fuss: 'summe' },
          { schluessel: 'beanstandet', titel: 'beanstandet', art: 'zahl', nachkomma: 0, fuss: 'summe' },
          {
            schluessel: 'dokumentnummer', titel: 'Dokumentnummer', wert: (b) => b.dokumentnummer,
            inhalt: (b) => <span className="schwach aus-fein">{b.dokumentnummer ?? 'noch kein Bericht erzeugt'}</span>,
          },
          {
            schluessel: 'aktuell', titel: 'Stand des Dokuments', art: 'status',
            wert: (b) => (!b.dokumentnummer ? null : b.aktuell ? 'zum jüngsten Lauf' : 'überholt'),
            inhalt: (b) => (!b.dokumentnummer
              ? <span className="schwach">—</span>
              : b.aktuell
                ? <StatusBadge zustand="gut" text="zum jüngsten Lauf" klein />
                : <StatusBadge zustand="warnung" text="überholt — seither neu gerechnet" klein />),
          },
          {
            schluessel: 'export', titel: 'Export', sortierbar: false,
            inhalt: (b) => (b.hatLauf
              ? <AussenBerichtKnopf api={api} projektId={projektId} bereich={b.bereich} stand={b} klein />
              : <span className="schwach aus-fein">erst rechnen</span>),
          },
        ]}
        zeilen={berichte}
        schluessel={(b) => b.bereich}
        aktiveZeile={gewaehlt}
        leer={<EmptyState klein titel="Kein Außenbereich gemeldet." />}
        suche={false}
        dicht
      />
    </ContentSection>
  );
}

function Blatt({ lauf, bereich }: {
  lauf: AussenBerechnungslauf; bereich: string;
}) {
  const z = (w: unknown, n = 2): string => {
    if (w === null || w === undefined) return '—';
    if (typeof w === 'boolean') return w ? 'ja' : 'nein';
    // TS9a: über das Muster - genau n Stellen wie bisher.
    if (typeof w === 'number') return formatiereZahl(w, n);
    return String(w);
  };

  return (
    <>
      {/* --- Das Ergebnis --- */}
      <ContentSection
        titel={BEREICHE.find((b) => b[0] === bereich)?.[1]
          ?? bereichname(bereich)}
        beschreibung={lauf.norm}
        meta={(
          <>
            Gerechnet am {zeitpunkt(lauf.gerechnetAm)}
            {lauf.gerechnetVon && ` von ${lauf.gerechnetVon}`}
          </>
        )}
      >
        {lauf.kennzahlen && (
          <KpiReihe>
            {Object.entries(lauf.kennzahlen)
              .filter(([, v]) => typeof v !== 'object')
              .map(([k, v]) => (
                <KpiCard key={k} titel={lesbar(k)} wert={z(v, 1)} />
              ))}
            {lauf.beanstandet > 0 && (
              <KpiCard
                titel="beanstandet" wert={lauf.beanstandet}
                vergleich={`von ${lauf.zeilen} Zeilen`}
                zustand="warnung" zustandText="prüfen"
              />
            )}
          </KpiReihe>
        )}
      </ContentSection>

      {/* --- Die Annahmen --- */}
      {lauf.annahmen && lauf.annahmen.length > 0 && (
        <ContentSection titel="Grundlagen der Rechnung">
          <p className="aus-hinweis">
            Die Herkunft ist keine Zierde: <strong>norm</strong> heißt, der
            Wert steht in einer Norm; <strong>auftrag</strong>, der Bauherr hat
            ihn festgelegt; <strong>festlegung</strong>, der Fachplaner hat auf
            eine benannte Frage geantwortet — dann steht die Fundstelle
            daneben; <strong>angenommen</strong>, wir haben ihn gesetzt. Nur
            das Letzte ist eine Annahme im eigentlichen Sinn — und sie trägt
            die Unsicherheit des Ergebnisses.
          </p>

          {/*
            Die Zahl der Festlegungen steht über der Tabelle und nicht nur in
            ihr: Ein Lauf vor und ein Lauf nach den Antworten des Fachplaners
            sähen sonst gleich aus, und genau das ist der Unterschied
            zwischen einem Vorgabewert und einem Nachweis.
          */}
          {lauf.annahmen.some((a) => a.herkunft === 'festlegung') && (
            <p className="aus-fein">
              <strong>
                {lauf.annahmen.filter((a) => a.herkunft === 'festlegung').length}
                {' '}von {lauf.annahmen.length}
              </strong>{' '}
              Werten sind seit dem <strong>16.09.2026</strong> durch den
              Fachplaner bestätigt.
            </p>
          )}

          <DataTable
            name="Grundlagen"
            spalten={[
              {
                schluessel: 'groesse', titel: 'Größe', immerSichtbar: true,
                wert: (a: any) => lesbar(a.schluessel),
              },
              {
                // Ein Textwert ist auch ein Wert: „SML" und „bemessung" haben
                // keine Zahl, und eine leere Zelle sähe aus, als fehlte die
                // Festlegung.
                schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts',
                wert: (a: any) => (a.wert ?? a.text),
                inhalt: (a: any) => (a.wert !== null && a.wert !== undefined
                  ? <WertEinheit wert={a.wert} nachkomma={3} einheit={a.einheit ?? undefined} rechts />
                  : <>{a.text ?? '—'}</>),
              },
              { schluessel: 'einheit', titel: 'Einheit', wert: (a: any) => a.einheit },
              { schluessel: 'norm', titel: 'Norm', wert: (a: any) => a.norm },
              {
                schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
                breite: '210px',
                wert: (a: any) => `${herkunftname(a.herkunft)} ${a.herkunft}${
                  a.istBestaetigt ? ' · bestätigt' : ''}`,
                inhalt: (a: any) => (a.herkunft === 'angenommen'
                  ? <StatusBadge zustand="warnung" text="angenommen" klein />
                  : a.istBestaetigt
                    ? <StatusBadge zustand="gut"
                                   text={`${herkunftname(a.herkunft)} · bestätigt`} klein />
                    : <StatusBadge zustand="info"
                                   text={herkunftname(a.herkunft)} klein />),
              },
              {
                schluessel: 'quelle', titel: 'Quelle', wert: (a: any) => a.quelle,
                inhalt: (a: any) => (
                  <span className="schwach aus-fein">{a.quelle ?? '—'}</span>
                ),
              },
            ]}
            zeilen={lauf.annahmen}
            schluessel={(a: any) => String(a.schluessel)}
            ausfuhr
            ausfuhrDateiname={`aussen-annahmen-${bereich}`}
            dicht
          />
        </ContentSection>
      )}

      {/* --- Die Zeilen mit ihren Teilstrecken --- */}
      {lauf.zeilen2.map((zeile) => (
        <Zeile key={zeile.id} zeile={zeile} bereich={bereich} z={z} />
      ))}

      {/* --- Die Hinweise --- */}
      {lauf.hinweise && lauf.hinweise.length > 0 && (
        <ContentSection titel="Hinweise">
          {lauf.hinweise.map((h, i) => (
            /^(ACHTUNG|ANNAHME|NAEHERUNG|BEFUND)/.test(h)
              ? <p key={i}>
                  <StatusBadge zustand="warnung" text={h} />
                </p>
              : <p key={i}>{h}</p>
          ))}
        </ContentSection>
      )}
    </>
  );
}

function Zeile({ zeile, bereich, z }: {
  zeile: AussenBerechnungslauf['zeilen2'][number];
  bereich: string;
  z: (w: unknown, n?: number) => string;
}) {
  const spaltenbeschreibung = SPALTEN[bereich] ?? [];
  const teile = zeile.teilstrecken ?? [];

  // Vorher eine Kappung auf die ersten 25 Teilstrecken mit dem Knopf „alle N
  // zeigen". Beides entfällt: `DataTable` virtualisiert ab 200 Zeilen (Paket U6b).
  //
  // Die Beschriftungen tragen die Einheit in eckigen Klammern (`di [mm]`).
  // Sie wird hier herausgelöst und als Spalteneinheit gesetzt - so steht sie
  // einmal am Kopf und nicht in jeder Zelle (Abschnitt 15).
  const spalten: Spalte<any>[] = spaltenbeschreibung.map(([k, titel, n]) => {
    const treffer = /^(.*?)\s*\[(.+)\]$/.exec(titel);
    const reinerTitel = treffer ? treffer[1] : titel;
    const einheit = treffer ? treffer[2] : undefined;

    // Vier Felder der Teilstrecken tragen einen Datenbankschluessel und
    // keine Zahl: bewertung ('ohne_abfluss'), herkunft ('modell'), art
    // ('verkehrsflaeche'), herkunftFlaeche ('planangabe') und seite
    // ('vorlauf'). Sie standen bis zum 23.09.2026 roh in der Zelle.
    const uebersetzer: Record<string, (w: string) => string> = {
      bewertung: bewertungname,
      herkunft: herkunftname,
      herkunftFlaeche: herkunftname,
      art: artname,
      seite: seitenname,
    };

    const klartext: ((w: string) => string) | undefined = uebersetzer[k];

    return {
      schluessel: k,
      titel: reinerTitel,
      einheit,
      art: n === undefined ? undefined : 'zahl',
      nachkomma: n,
      // Der SUCHWERT bleibt der Rohwert daneben: Wer nach 'ohne_abfluss'
      // sucht, weil es so im Bericht seines Vorgaengers stand, soll es
      // finden.
      wert: (t: any) => (klartext !== undefined && typeof t[k] === 'string'
        ? `${klartext(t[k])} ${t[k]}` : t[k]),
      inhalt: (t: any) => (klartext !== undefined && typeof t[k] === 'string'
        ? <>{klartext(t[k])}</>
        : <>{z(t[k], n ?? 2)}</>),
    } as Spalte<any>;
  });

  const kopfwerte = Object.entries(zeile.werte ?? {})
    .filter(([, v]) => typeof v !== 'object')
    .map(([k, v]) => ({ beschriftung: lesbar(k), wert: z(v, 2) }));

  return (
    <ContentSection
      titel={zeile.bezeichnung ?? '—'}
      aufklappbar
      standardOffen={zeile.istMassgebend}
      beschreibung={zeile.befund ?? undefined}
      meta={(
        <>
          {zeile.kreis && (
            <span className="schwach aus-fein">{kreisname(zeile.kreis)}{' '}</span>
          )}
          {zeile.revitElementId && (
            <span className="schwach aus-fein">Element {zeile.revitElementId}{' '}</span>
          )}
          {/* Drei Zustaende und nicht zwei: Eine Nachweisluecke
              ('ohne_abfluss', 'nicht_pruefbar') ist kein Mangel, und wer
              sie als Warnung setzt, faerbt 344 von 1.026 Straengen gelb. */}
          <StatusBadge {...bewertungStatus(zeile.bewertung)} klein />
        </>
      )}
    >
      {kopfwerte.length > 0 && (
        <PropertyGrid spalten={3} dicht eintraege={kopfwerte} />
      )}

      {teile.length > 0 && spalten.length > 0 && (
        <DataTable
          name={`Teilstrecken (${teile.length})`}
          spalten={spalten}
          zeilen={teile.map((t, i) => ({ ...t, _nr: i }))}
          schluessel={(t: any) => String(t._nr)}
          zeilenKlasse={(t: any) => (t.bewertung && t.bewertung !== 'ok'
            ? 'datenfeld' : undefined)}
          ausfuhr
          ausfuhrDateiname={`aussen-${bereich}-${zeile.bezeichnung ?? 'strang'}`}
          dicht
        />
      )}

      {zeile.rechenweg && zeile.rechenweg.length > 0 && (
        <>
          <h3 className="aus-rechenweg-titel">Rechenweg</h3>
          <ol className="aus-rechenweg">
            {zeile.rechenweg.map((x, i) => <li key={i}>{x}</li>)}
          </ol>
        </>
      )}
    </ContentSection>
  );
}

/**
 * Aus 'verlustGesamtKwhA' wird 'Verlust gesamt [kWh/a]'.
 *
 * Der Wächter oben ist nicht überflüssig: Diese Zeile hat die ganze Seite
 * zum Absturz gebracht, als die API die Schlüssel groß auslieferte und
 * `name` undefined ankam. Der Fehler lag anderswo — aber eine Funktion, die
 * nur eine Beschriftung hübsch macht, darf die Berechnung nicht mitreißen.
 */
function lesbar(name: string | null | undefined): string {
  if (!name) return '—';

  const t = String(name)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/ Ls$/, ' [l/s]')
    .replace(/ M3$/, ' [m³]')
    .replace(/ M2$/, ' [m²]')
    .replace(/ Mm$/, ' [mm]')
    .replace(/ M3h$/, ' [m³/h]')
    .replace(/ LMin$/, ' [l/min]')
    .replace(/ KwhA$/, ' [kWh/a]')
    .replace(/ WM$/, ' [W/m]')
    .replace(/ Bar$/, ' [bar]')
    .replace(/ C$/, ' [°C]')
    .replace(/ W$/, ' [W]')
    .replace(/ M$/, ' [m]');

  return t.charAt(0).toUpperCase() + t.slice(1);
}
